import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { PaymentTransactionListResponse, PaymentTransactionReceipt } from '../../../api/types';
import { useAuth } from '../../../app/providers/useAuth';
import { fetchTransactionReceipt, fetchTransactions } from '../api/billingApi';

// PARA-140 (AC-4/AC-5) — история платежей. Пагинация limit/offset (курсорной
// нет — out of scope story).

/** Дефолт страницы совпадает с дефолтом backend (limit=20, max 100). */
export const TRANSACTIONS_PAGE_SIZE = 20;

/** Одна страница истории «как есть»: `transactions` + `has_more` из ответа.
 *
 * Хук намеренно ничего не накапливает и не знает про «следующую страницу»:
 * склейка загруженных страниц и логика кнопки «Показать ещё» живут в
 * `TransactionHistoryScreen` — он владеет `offset`. Признак продолжения —
 * только `has_more` из ответа; эвристика «страница пришла полной» врала бы
 * ровно на кратных размерах истории. */
export function usePaymentTransactions(
  enabled: boolean,
  limit: number,
  offset: number,
): UseQueryResult<PaymentTransactionListResponse> {
  const { user } = useAuth();

  return useQuery({
    // offset в ключе обязателен: страницы кешируются раздельно, иначе вторая
    // страница подменила бы первую в кеше одного и того же ключа.
    queryKey: ['billing', 'transactions', user?.id, limit, offset],
    queryFn: () => fetchTransactions(limit, offset),
    // История грузится лениво: только когда экран открыт (enabled=true).
    enabled: user !== null && enabled,
  });
}

/** Квитанция выбранной транзакции; null — квитанция не запрошена. */
export function usePaymentTransactionReceipt(
  transactionId: number | null,
): UseQueryResult<PaymentTransactionReceipt> {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['billing', 'transactions', user?.id, 'receipt', transactionId],
    queryFn: () => fetchTransactionReceipt(transactionId as number),
    enabled: user !== null && transactionId !== null,
  });
}
